/**
 * Универсальная модалка форм поддержки и «Предложить модель»
 * POST идёт на /api/support либо /api/suggest-model в зависимости от
 * варианта (см. SupportKind). Бэк уже пишет support_tickets, админ
 * увидит заявку в /admin/tickets.
 */
import { useEffect, useState } from "react";
import { Loader2, MailQuestion, Sparkles } from "lucide-react";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription, DialogClose,
} from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { useMutation } from "@tanstack/react-query";
import { api, ApiHttpError } from "@/lib/api";

export type SupportKind = "support" | "suggest_model";

interface Props {
  open: boolean;
  kind: SupportKind;
  onOpenChange: (open: boolean) => void;
}

const TITLE: Record<SupportKind, { title: string; subtitle: string; subjectLabel: string; bodyLabel: string; bodyPlaceholder: string; icon: typeof MailQuestion }> = {
  support: {
    title: "Связаться с поддержкой",
    subtitle: "Опишите проблему, мы ответим в ближайшее время",
    subjectLabel: "Тема",
    bodyLabel: "Сообщение",
    bodyPlaceholder: "Что произошло? Если есть ID запроса - приложите",
    icon: MailQuestion,
  },
  suggest_model: {
    title: "Предложить модель",
    subtitle: "Какую модель вам не хватает в каталоге?",
    subjectLabel: "Название модели",
    bodyLabel: "Зачем она нужна",
    bodyPlaceholder: "Например: для генерации логотипов в плоском стиле",
    icon: Sparkles,
  },
};

export function SupportDialog({ open, kind, onOpenChange }: Props) {
  const meta = TITLE[kind];
  const Icon = meta.icon;
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) { setSubject(""); setBody(""); setSent(false); setError(null); }
  }, [open, kind]);

  const mut = useMutation({
    mutationFn: (payload: { subject: string; body: string }) => {
      if (kind === "suggest_model") {
        return api.post<{ id: string }>("/suggest-model", { name: payload.subject, why: payload.body });
      }
      return api.post<{ id: string }>("/support", payload);
    },
  });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await mut.mutateAsync({ subject: subject.trim(), body: body.trim() });
      setSent(true);
    } catch (e) {
      const code = e instanceof ApiHttpError ? e.code : "http_error";
      setError(code === "blocked_content" ? "Сообщение отклонено модерацией" : "Не удалось отправить");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogTitle className="inline-flex items-center gap-2">
          <span className="grid h-7 w-7 place-items-center rounded-lg bg-brand-50 text-brand-700">
            <Icon className="h-4 w-4" />
          </span>
          {meta.title}
        </DialogTitle>
        <DialogDescription>{meta.subtitle}</DialogDescription>

        {sent ? (
          <div className="mt-4 space-y-4">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800">
              {kind === "suggest_model"
                ? "Спасибо! Мы рассмотрим вашу заявку"
                : "Спасибо! Мы получили обращение и ответим как можно скорее"}
            </div>
            <div className="flex justify-end">
              <DialogClose asChild>
                <Button type="button">Закрыть</Button>
              </DialogClose>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-3">
            <label className="block space-y-1">
              <span className="text-xs font-medium text-ink-mute">{meta.subjectLabel}</span>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required maxLength={200}
              />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-ink-mute">{meta.bodyLabel}</span>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={meta.bodyPlaceholder}
                rows={6} required maxLength={4000}
                className="w-full rounded-xl border border-line bg-surface p-3 text-sm text-ink placeholder:text-ink-soft focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition resize-y"
              />
            </label>
            {error && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <DialogClose asChild>
                <Button type="button" variant="ghost">Отмена</Button>
              </DialogClose>
              <Button
                type="submit"
                disabled={mut.isPending || !subject.trim() || !body.trim()}
                className="gap-2"
              >
                {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Отправить
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
